// UserListSidebar.tsx
import React, { useEffect, useState } from "react";
import { WhatsappTemplateSchema } from "./WhatsappContext";

export interface ChatUser {
    id?: number;
    wa_id: string;
    name: string;
    profile_name?: string | null;
    profile_pic_url?: string | null;
    country_code?: string | null;
    language_code?: string | null;
    source?: string | null;
    is_blocked?: boolean;
    is_active?: boolean;
    last_message_at?: string | null;
    last_incoming_at?: string | null;
    last_outgoing_at?: string | null;
    last_seen_at?: string | null;
    extra_data?: unknown;
    created_at?: string | null;
    updated_at?: string | null;
    last_message?: string;
    avatarUrl?: string;
    unread_count?: number;
    profile?: {
        userId?: string;
        name?: string;
        email?: string;
        phone?: string;
        gender?: string;
        age?: number;
        status?: string;
    } | null;
}


interface Props {
  users: ChatUser[];
  selectedUser: ChatUser | null;
  onSelect: (user: ChatUser) => void;
  onSendTemplate: (args: { user: ChatUser; templateKey: string; parameters: Record<string, string> }) => Promise<boolean>;
  onRefresh: () => void;
  templates: Record<string, WhatsappTemplateSchema>;
  onFetchTemplates: () => void;
  unreadSummary: any;
  onFetchUnreadSummary: () => void;
  usersLoading: boolean;
  usersHasMore: boolean;
  searchTerm: string;
  onSearch: (search: string) => void;
  onLoadMore: () => void;
}

const UserListSidebar: React.FC<Props> = ({
  users,
  onSelect,
  selectedUser,
  onSendTemplate,
  onRefresh,
  templates,
  onFetchTemplates,
  unreadSummary,
  onFetchUnreadSummary,
  usersLoading,
  usersHasMore,
  searchTerm: serverSearchTerm,
  onSearch,
  onLoadMore
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [name, setName] = useState("");
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({});
  const [templateError, setTemplateError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");
  const [searchTerm, setSearchTerm] = useState(serverSearchTerm || "");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const selectedTemplate = selectedTemplateKey ? templates[selectedTemplateKey] : undefined;

  useEffect(() => {
    setSearchTerm(serverSearchTerm || "");
  }, [serverSearchTerm]);

  useEffect(() => {
    if (!isModalOpen) return;
    onFetchTemplates();
  }, [isModalOpen]);

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateParams({});
      return;
    }
    const defaults: Record<string, string> = {};
    selectedTemplate.parameters?.forEach((parameter) => {
      defaults[parameter.key] = parameter.default || "";
    });
    setTemplateParams(defaults);
  }, [selectedTemplateKey, selectedTemplate]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSend = async () => {
    let hasError = false;

    // Clear previous errors
    setPhoneError("");
    setNameError("");
    setTemplateError("");

    if (!phoneNumber) {
      setPhoneError("Phone number is required.");
      hasError = true;
    } else if (phoneNumber.length !== 10) {
      setPhoneError("Phone number must be exactly 10 digits.");
      hasError = true;
    }

    if (!name.trim()) {
      setNameError("Name is required.");
      hasError = true;
    }

    if (!selectedTemplateKey) {
      setTemplateError("Template is required.");
      hasError = true;
    } else if (selectedTemplate?.parameters?.some((parameter) => parameter.required && !templateParams[parameter.key]?.trim())) {
      setTemplateError("Please fill all required template parameters.");
      hasError = true;
    }

    if (hasError) return;

    const newUser: ChatUser = {
      wa_id: phoneNumber,
      name: name.trim(),
      last_message: "Template message sent",
    };

    const res = await onSendTemplate({
      user: newUser,
      templateKey: selectedTemplateKey,
      parameters: templateParams
    });
    if (res) {
      // Reset form state
      setPhoneNumber("");
      setName("");
      setSelectedTemplateKey("");
      setTemplateParams({});
      
      // Clear all errors
      setPhoneError("");
      setNameError("");
      setTemplateError("");
      
      setIsModalOpen(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) {
      setPhoneError("Phone number cannot be more than 10 digits.");
      return;
    }
    setPhoneNumber(value);
    if (value.length === 10) {
      setPhoneError("");
    }
  };

  const formatLastMessageTime = (user: ChatUser) => {
    const raw = user.last_message_at || user.updated_at;
    if (!raw) return "";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const sameDay =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (sameDay) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { day: "2-digit", month: "short" });
  };

  const unreadIdsFromSummary = (() => {
    if (!unreadSummary) return new Set<string>();

    const usersList =
      unreadSummary?.users ||
      unreadSummary?.unread_users ||
      unreadSummary?.details ||
      unreadSummary?.data?.users ||
      [];

    if (!Array.isArray(usersList)) return new Set<string>();
    return new Set(
      usersList
        .map((u: any) => (u?.wa_id || u?._id || u?.user_id || "").toString())
        .filter(Boolean)
    );
  })();

  const visibleUsers = unreadOnly
    ? users.filter((u) => (u.unread_count || 0) > 0 || unreadIdsFromSummary.has(u.wa_id))
    : users;

  return (
    <div className="relative w-full md:w-80 lg:w-96 h-full bg-slate-950 border-r border-slate-800 flex flex-col shadow-xl z-20">
      {/* Header */}
      <div className="bg-slate-900/50 backdrop-blur-md p-5 border-b border-slate-800 sticky top-0 z-30">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Messages</h1>
            <p className="text-xs text-slate-400 font-medium tracking-wide mt-1">ADMIN CONSOLE</p>
          </div>
          <div className="flex space-x-2">
            <button
              className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all ${
                unreadOnly
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "text-slate-400 hover:text-white hover:bg-slate-800 border-slate-700"
              }`}
              onClick={() => {
                const next = !unreadOnly;
                setUnreadOnly(next);
                if (next) {
                  onFetchUnreadSummary();
                } else {
                  onRefresh();
                }
              }}
              title="Toggle unread only"
            >
              Unread
            </button>
            <button
              className="p-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all duration-300"
              onClick={onRefresh}
              title="Refresh List"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-500/20 transition-all duration-300 hover:scale-105"
              onClick={() => setIsModalOpen(true)}
              title="New Chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
             <svg className="w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
             </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search conversations..."
            className="w-full pl-10 pr-10 py-3 text-sm bg-slate-900 border border-slate-700 text-slate-200 placeholder-slate-500 rounded-xl focus:border-blue-500/50 focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-200 transition-colors"
              title="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Chat list */}
      <div
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent p-3 space-y-1"
        onScroll={(e) => {
          const target = e.currentTarget;
          const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
          if (distanceFromBottom < 80 && usersHasMore && !usersLoading) {
            onLoadMore();
          }
        }}
      >
        {visibleUsers.map((user) => {
          const isActive = selectedUser?.wa_id === user.wa_id;
          return (
            <div
              key={user.wa_id}
              onClick={() => onSelect(user)}
              className={`cursor-pointer group px-4 py-3.5 rounded-xl border border-transparent transition-all duration-200 relative overflow-hidden ${
                isActive 
                  ? "bg-blue-600/10 border-blue-500/20 shadow-sm" 
                  : "hover:bg-slate-900 hover:border-slate-800"
              }`}
            >
              <div className="flex items-center space-x-4 relative z-10">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className={`w-12 h-12 rounded-full object-cover ring-2 ${isActive ? 'ring-blue-500' : 'ring-slate-700'} group-hover:ring-slate-600 transition-all`}
                    />
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shadow-inner ring-2 ${
                        isActive 
                            ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white ring-blue-500/50' 
                            : 'bg-slate-800 text-slate-400 ring-slate-800 group-hover:bg-slate-700 group-hover:ring-slate-700'
                    }`}>
                      {user?.name?.substring(0, 1)?.toUpperCase()}
                    </div>
                  )}
                  {/* Active Dot */}
                  {user.unread_count && user.unread_count > 0 ? (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-slate-950"></span>
                      </span>
                  ) : isActive && (
                      <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-slate-950 rounded-full"></span>
                  )}
                </div>
                
                {/* User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`font-semibold text-base truncate pr-2 transition-colors ${isActive ? 'text-white' : 'text-slate-200 group-hover:text-white'}`}>
                      {user.name}
                    </h3>
                    <span className="text-[10px] text-slate-500 font-medium">
                      {formatLastMessageTime(user)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-mono truncate mb-0.5 ${isActive ? 'text-blue-400' : 'text-slate-400 group-hover:text-slate-300'}`}>
                        +91 {user.wa_id}
                      </p>
                      <p className={`text-sm truncate pr-2 transition-colors ${isActive ? 'text-blue-200' : 'text-slate-500 group-hover:text-slate-400'}`}>
                        {user.last_message ? (
                          user.last_message.length > 30 ? 
                          `${user.last_message.slice(0, 30)}...` : 
                          user.last_message
                        ) : (
                          <span className="italic opacity-50">New contact</span>
                        )}
                      </p>
                    </div>
                    {user.unread_count && user.unread_count > 0 && (
                      <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        {user.unread_count > 99 ? '99+' : user.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Empty state */}
        {visibleUsers.length === 0 && !usersLoading && (
          <div className="flex flex-col items-center justify-center h-48 text-slate-600">
            <div className="p-4 bg-slate-900/50 rounded-full mb-3">
                <svg className="w-8 h-8 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            </div>
            <p className="text-sm font-medium">No contacts found</p>
          </div>
        )}
        {usersLoading && (
          <div className="py-3 text-center text-xs text-slate-400">Loading conversations...</div>
        )}
      </div>

      {/* Modal - Modern Dark */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-sm shadow-2xl relative overflow-hidden">
            {/* Ambient background glow */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <div className="p-6 relative z-10">
              <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-3">
                <div className="bg-blue-600 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
                </div>
                Start New Chat
              </h2>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Contact Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. John Doe"
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-slate-600"
                  />
                  {nameError && <p className="text-xs text-red-500 mt-1.5 ml-1">{nameError}</p>}
                </div>

                <div>
                   <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Phone Number</label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="10 digit number"
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-slate-600"
                  />
                  {phoneError && <p className="text-xs text-red-500 mt-1.5 ml-1">{phoneError}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Template</label>
                  <select
                    value={selectedTemplateKey}
                    onChange={(e) => setSelectedTemplateKey(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select template</option>
                    {Object.entries(templates).map(([templateKey, template]) => (
                      <option key={templateKey} value={templateKey}>
                        {templateKey} ({template.template_name})
                      </option>
                    ))}
                  </select>
                  {/* {selectedTemplate && (
                    <p className="text-[11px] text-slate-400 mt-2 ml-1">
                      {selectedTemplate.description || "No description available"}
                    </p>
                  )} */}
                  {templateError && <p className="text-xs text-red-500 mt-1.5 ml-1">{templateError}</p>}
                </div>

                {selectedTemplate && (
                  <div className="space-y-3">
                    {selectedTemplate.description && (
                      <p className="text-xs text-slate-400 px-1">{selectedTemplate.description}</p>
                    )}
                    {selectedTemplate.parameters?.map((parameter) => (
                      <div key={parameter.key}>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
                          {parameter.label} {parameter.required ? "*" : ""}
                        </label>
                        <input
                          type="text"
                          value={templateParams[parameter.key] || ""}
                          onChange={(e) =>
                            setTemplateParams((prev) => ({
                              ...prev,
                              [parameter.key]: e.target.value
                            }))
                          }
                          placeholder={parameter.default || parameter.key}
                          className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-slate-600"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  className="px-5 py-2.5 text-sm font-semibold text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  onClick={() => {
                    setIsModalOpen(false);
                    setPhoneNumber("");
                    setName("");
                    setSelectedTemplateKey("");
                    setTemplateParams({});
                    setPhoneError("");
                    setNameError("");
                    setTemplateError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg shadow-lg shadow-blue-500/25 transition-all hover:-translate-y-0.5"
                  onClick={handleSend}
                >
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserListSidebar;
